import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import { useApiError } from "../../hooks/useApiError";
import { validateEmail } from "../../utils/validation";
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Avatar,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
  showRegister?: boolean; // Show register option only for students
  isStudent?: boolean; // If true, show Mobile no. instead of Email
}

const LoginModal: React.FC<LoginModalProps> = ({ open, onClose, showRegister = false, isStudent = false }) => {
  const navigate = useNavigate();
  const { login, forgotPassword, verifyOtp, resetPassword } = useAuth();
  const { showSuccess, showError } = useError();
  const { handleLoginError } = useApiError();
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [otpVerificationOpen, setOtpVerificationOpen] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const [isPasswordResetMode, setIsPasswordResetMode] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState<boolean | null>(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [emailError, setEmailError] = useState<string>("");
  const [mobileError, setMobileError] = useState<string>("");

  // Function to clear image cache and force reload of images
  const clearImageCache = () => {
    if ("caches" in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    const images = document.querySelectorAll("img");
    images.forEach((img) => {
      if (img.src && !img.src.includes("?v=")) {
        const separator = img.src.includes("?") ? "&" : "?";
        img.src = img.src + separator + "v=" + Date.now();
      }
    });
  };

  // React Query mutation
  const mutation = useMutation({
    mutationFn: login,
    onSuccess: (userData) => {
      showSuccess("Login successful!");
      clearImageCache();
      
      // Close modal
      onClose();
      
      // Navigate to appropriate dashboard
      if (userData.roles.includes("ADMIN")) {
        navigate("/admin/dashboard");
      } else if (userData.roles.includes("SCHOOL_ADMIN")) {
        navigate("/school-admin/dashboard");
      } else if (userData.roles.includes("APP_STUDENT")) {
        navigate("/student-school-selection");
      } else if (userData.roles.includes("STUDENT")) {
        navigate("/student/dashboard");
      } else if (userData.roles.includes("TEACHER")) {
        navigate("/teacher/dashboard");
      } else {
        navigate("/dashboard");
      }
    },
    onError: (error) => {
      handleLoginError(error);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email for staff/admin
    if (!isStudent) {
      if (!credentials.email.trim()) {
        setEmailError("Email is required");
        return;
      }
      if (!validateEmail(credentials.email.trim())) {
        setEmailError("Please enter a valid email address");
        return;
      }
      setEmailError("");
    }
    
    // Validate mobile number for students
    if (isStudent) {
      if (!credentials.email.trim()) {
        setMobileError("Mobile number is required");
        return;
      }
      const mobile = credentials.email.trim();
      if (!/^[6-9]/.test(mobile)) {
        setMobileError("Mobile number must start with 6, 7, 8, or 9");
        return;
      }
      if (mobile.length !== 10) {
        setMobileError("Mobile number must be exactly 10 digits");
        return;
      }
      setMobileError("");
    }
    
    mutation.mutate(credentials);
  };

  const handleClose = () => {
    // Reset all state when closing
    setCredentials({ email: "", password: "" });
    setShowPassword(false);
    setForgotPasswordOpen(false);
    setForgotPasswordEmail("");
    setEmailError("");
    setMobileError("");
    setOtpVerificationOpen(false);
    setOtp(["", "", "", "", "", ""]);
    setIsPasswordResetMode(false);
    setNewPassword("");
    setConfirmPassword("");
    setPasswordMatch(null);
    setShowNewPassword(false);
    navigate("/");
    onClose();
  };

  const handleForgotPasswordClick = () => {
    setForgotPasswordOpen(true);
  };

  const handleForgotPasswordClose = () => {
    setForgotPasswordOpen(false);
    setForgotPasswordEmail("");
    setEmailError("");
  };

  const resetToForgotPasswordDialog = () => {
    setOtpVerificationOpen(false);
    setIsPasswordResetMode(false);
    setForgotPasswordOpen(true);
    setForgotPasswordEmail("");
    setEmailError("");
    setOtp(["", "", "", "", "", ""]);
    setNewPassword("");
    setConfirmPassword("");
    setPasswordMatch(null);
    setShowNewPassword(false);
  };

  const handleSendOTP = async () => {
    if (!forgotPasswordEmail.trim()) {
      setEmailError("Please enter your email address");
      return;
    }

    setEmailError("");
    setIsSendingOTP(true);
    
    try {
      const response = await forgotPassword(forgotPasswordEmail);
      
      if (response && (response.toLowerCase().includes("no user found") || 
          response.toLowerCase().includes("invalid email") ||
          response.toLowerCase().includes("user not found"))) {
        setEmailError("Invalid email. No user found with this email address. Please enter a valid email.");
        return;
      }
      
      showSuccess(response || "OTP sent successfully to your email!");
      setForgotPasswordOpen(false);
      setOtpVerificationOpen(true);
      setOtp(["", "", "", "", "", ""]);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          error?.response?.data || 
                          error?.message || 
                          "";
      
      if (errorMessage.toLowerCase().includes("no user found") || 
          errorMessage.toLowerCase().includes("invalid email") ||
          errorMessage.toLowerCase().includes("user not found")) {
        setEmailError("Invalid email. No user found with this email address. Please enter a valid email.");
      } else {
        showError(errorMessage || "Failed to send OTP. Please try again.");
        setEmailError("");
      }
    } finally {
      setIsSendingOTP(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      alert("Please enter all 6 digits of the OTP");
      return;
    }

    setIsVerifyingOTP(true);
    try {
      const response = await verifyOtp(forgotPasswordEmail, Number.parseInt(otpString, 10));
      showSuccess(response || "OTP verified successfully!");
      
      if (response && response.includes("reset your password")) {
        setIsPasswordResetMode(true);
        setOtp(["", "", "", "", "", ""]);
      } else {
        setOtpVerificationOpen(false);
        setForgotPasswordEmail("");
        setOtp(["", "", "", "", "", ""]);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          error?.response?.data || 
                          error?.message || 
                          "";
      
      if (errorMessage.toLowerCase().includes("no user found") || 
          errorMessage.toLowerCase().includes("invalid email") ||
          errorMessage.toLowerCase().includes("user not found")) {
        resetToForgotPasswordDialog();
        setEmailError("Invalid email. No user found with this email address. Please enter a valid email.");
      } else {
        showError(errorMessage || "Invalid OTP. Please try again.");
        setOtp(["", "", "", "", "", ""]);
      }
    } finally {
      setIsVerifyingOTP(false);
    }
  };

  const handleCloseOtpVerification = () => {
    setOtpVerificationOpen(false);
    setOtp(["", "", "", "", "", ""]);
    setIsPasswordResetMode(false);
    setNewPassword("");
    setConfirmPassword("");
    setPasswordMatch(null);
    setShowNewPassword(false);
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim()) {
      alert("Please enter a new password");
      return;
    }

    if (!confirmPassword.trim()) {
      alert("Please confirm your password");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMatch(false);
      return;
    }

    if (newPassword.length < 6) {
      alert("Password must be at least 6 characters long");
      return;
    }

    setIsResettingPassword(true);
    try {
      const response = await resetPassword(forgotPasswordEmail, newPassword);
      showSuccess(response || "Password reset successfully!");
      setOtpVerificationOpen(false);
      setIsPasswordResetMode(false);
      setForgotPasswordEmail("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordMatch(null);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          error?.response?.data || 
                          error?.message || 
                          "";
      
      if (errorMessage.toLowerCase().includes("no user found") || 
          errorMessage.toLowerCase().includes("invalid email") ||
          errorMessage.toLowerCase().includes("user not found")) {
        resetToForgotPasswordDialog();
        setEmailError("Invalid email. No user found with this email address. Please enter a valid email.");
      } else {
        showError(errorMessage || "Failed to reset password. Please try again.");
      }
    } finally {
      setIsResettingPassword(false);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Main Login Card */}
      <Card
        sx={{
          maxWidth: 450,
          width: "100%",
          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          borderRadius: 2,
          mx: 2,
          position: "relative",
        }}
      >
        {/* Close Icon Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
            color: "#666",
            transition: 'all 0.2s ease',
            '&:hover': {
              color: "#3092e3",
              transform: 'rotate(90deg) scale(1.1)',
              bgcolor: 'rgba(48, 146, 227, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <CardContent sx={{ p: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#3092e3",
                width: 70,
                height: 70,
                mb: 2,
              }}
            >
              <LockOutlinedIcon fontSize="large" />
            </Avatar>
            <Typography
              variant="h4"
              fontWeight="bold"
              gutterBottom
              sx={{
                textAlign: "center",
              }}
            >
              {isStudent ? "Student Login" : "School Login"}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
            >
              Welcome back! Please enter your credentials to access the dashboard.
            </Typography>
          </Box>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label={isStudent ? "Mobile no." : "Email"}
              type={isStudent ? "tel" : "email"}
              value={credentials.email.trim()}
              onChange={(e) => {
                let value = e.target.value;
                
                if (isStudent) {
                  // Allow only digits and limit to 10 digits
                  value = value.replace(/\D/g, "").slice(0, 10);
                  setCredentials({ ...credentials, email: value });
                  // Clear error when user starts typing
                  if (mobileError) {
                    setMobileError("");
                  }
                } else {
                  // For email, allow normal input
                  setCredentials({ ...credentials, email: value.trim() });
                  // Clear error when user starts typing
                  if (emailError) {
                    setEmailError("");
                  }
                }
              }}
              margin="normal"
              required
              autoFocus
              error={isStudent ? !!mobileError : !!emailError}
              helperText={isStudent ? mobileError : emailError}
              inputProps={isStudent ? { maxLength: 10 } : {}}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={credentials.password}
              onChange={(e) =>
                setCredentials({ ...credentials, password: e.target.value })
              }
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((show) => !show)}
                      edge="end"
                      sx={{ color: "#3092e3" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ textAlign: "right", mt: 1, mb: 2 }}>
              <Button
                variant="text"
                size="small"
                onClick={handleForgotPasswordClick}
                sx={{
                  textTransform: "none",
                  color: "#3092e3",
                  fontSize: "0.875rem",
                }}
              >
                Forgot Password?
              </Button>
            </Box>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={mutation.isLoading}
              sx={{
                mt: 4,
                mb: 3,
                py: 2,
                fontWeight: "bold",
                fontSize: 16,
                backgroundColor: "#3092e3",
              }}
            >
              {mutation.isLoading ? "Logging in..." : "Login"}
            </Button>
            {showRegister && (
              <Box sx={{ textAlign: "center", mt: 3 }}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {`Don't have an account? `}
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: "bold",
                      color: "#3092e3",
                    }}
                    onClick={() => {
                      handleClose();
                      navigate("/register");
                    }}
                  >
                    Register
                  </Button>
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Forgot Password Dialog */}
      <Dialog
        open={forgotPasswordOpen}
        onClose={handleForgotPasswordClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "1.5rem",
            pb: 1,
          }}
        >
          Forgot Password
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 3, textAlign: "center" }}
          >
            Enter your email address and we'll send you an OTP to reset your password.
          </Typography>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={forgotPasswordEmail}
            onChange={(e) => {
              setForgotPasswordEmail(e.target.value);
              if (emailError) {
                setEmailError("");
              }
            }}
            required
            error={!!emailError}
            helperText={emailError}
            sx={{
              "& .MuiFormHelperText-root": {
                color: emailError ? "error.main" : "text.secondary",
                fontWeight: emailError ? "bold" : "normal",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleForgotPasswordClose}
            disabled={isSendingOTP}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendOTP}
            variant="contained"
            disabled={isSendingOTP || !forgotPasswordEmail.trim()}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              px: 3,
            }}
          >
            {isSendingOTP ? "Sending..." : "Send OTP"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* OTP Verification Dialog */}
      <Dialog
        open={otpVerificationOpen}
        onClose={handleCloseOtpVerification}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "1.5rem",
            pb: 1,
          }}
        >
          {isPasswordResetMode ? "Reset Password" : "Verify OTP"}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {isPasswordResetMode ? (
            <>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3, textAlign: "center" }}
              >
                Enter your new password for <strong>{forgotPasswordEmail}</strong>
              </Typography>
              
              <TextField
                fullWidth
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new Password"
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        sx={{ color: "#3092e3" }}
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new Password"
                margin="normal"
                required
                error={passwordMatch === false}
                helperText={passwordMatch === false ? "Passwords do not match" : ""}
                sx={{
                  "& .MuiFormHelperText-root": {
                    color: passwordMatch === false ? "red" : "inherit",
                    fontWeight: passwordMatch === false ? "bold" : "normal",
                  },
                }}
              />
            </>
          ) : (
            <>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3, textAlign: "center" }}
              >
                Enter the 6-digit OTP sent to <strong>{forgotPasswordEmail}</strong>
              </Typography>
              
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  mb: 3,
                }}
              >
                {otp.map((digit, index) => (
                  <TextField
                    key={index}
                    id={`otp-${index}`}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    inputProps={{
                      maxLength: 1,
                      style: {
                        textAlign: "center",
                        fontSize: "1.5rem",
                        fontWeight: "bold",
                      },
                    }}
                    sx={{
                      width: 50,
                    }}
                  />
                ))}
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", opacity: 0.7 }}
              >
                Didn't receive the OTP?{" "}
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setOtpVerificationOpen(false);
                    setForgotPasswordOpen(true);
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: "bold",
                    color: "#3092e3",
                  }}
                >
                  Resend OTP
                </Button>
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCloseOtpVerification}
            disabled={isVerifyingOTP || isResettingPassword}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={isPasswordResetMode ? handleResetPassword : handleVerifyOTP}
            variant="contained"
            disabled={
              isPasswordResetMode
                ? isResettingPassword || !newPassword.trim() || !confirmPassword.trim()
                : isVerifyingOTP || otp.join('').length !== 6
            }
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              px: 3,
            }}
          >
            {isPasswordResetMode
              ? isResettingPassword
                ? "Resetting..."
                : "Reset Password"
              : isVerifyingOTP
              ? "Verifying..."
              : "Verify OTP"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LoginModal;
